import React from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import { Typography, Stack, Box } from "@mui/material";

// 画像アセット（必要に応じて存在するパスに差し替えてください）
import featureImage from "url:src/assets/images/speech_bubble.png";
import pattern_B from "url:src/assets/images/pattern_B.png";
import pattern_C from "url:src/assets/images/pattern_C.png";
import pattern_D from "url:src/assets/images/pattern_D.png";
import pattern_F from "url:src/assets/images/pattern_F.png";

/* ===========================
 * 型定義
 * =========================== */

export type VariantId = "existing" | "designA" | "designB" | "designC" | "designD" | "designE" | "designF";

export type CtaPlacement = "above-bullets" | "below-bullets";
export type CtaAlign = "left" | "center" | "right";

export type RichTextSpan = { text: string; sx?: SxProps<Theme> };
export type RichImageSpan = { imageSrc: string; alt?: string; sx?: SxProps<Theme> };
export type RichSpan = RichTextSpan | RichImageSpan;
export type RichLine = RichSpan[];
export type RichBlock = RichLine[];

// バリアントごとの宣言的設定
export type VariantConfig = {
  id: VariantId;
  bg?: {
    /** 背景画像（parcel 等の url: ローダ前提のURL文字列） */
    image?: string;
    /** 背景の追加スタイル（グラデ等） */
    sx?: SxProps<Theme>;
  };
  headline: RichBlock;
  bullets: RichLine[];
  cta: {
    placement: CtaPlacement;
    align: CtaAlign;
    /** CTA ボタン側に付与する className のサフィックス（例: "fv"） */
    classSuffix?: string;
  };
  /** FV ラッパーの個別調整（高さなど） */
  containerSx?: SxProps<Theme>;
  bulletsSx?: SxProps<Theme>;
  ctaGap?: number; 
};

type Weights = Partial<Record<VariantId, number>>;

type Options = {
  storageKey?: string;
  experimentId?: string;
  weights?: Weights;
  /** 外部から VariantConfig 群を差し替えたい場合に使用 */
  variants?: VariantConfig[];
};

const VARIANT_IDS: VariantId[] = [
  "existing",
  "designA",
  "designB",
  "designC",
  "designD",
  "designE",
  "designF",
] as const;

/* ===========================
 * ユーティリティ
 * =========================== */

/** SSR や Safari プライバシー設定等で localStorage が使えない環境対策 */
function safeLocalStorageGet(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeLocalStorageSet(key: string, value: string): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, value);
  } catch {
    /* 特に何もしない */
  }
}

function normalizeWeights(input?: Weights): Record<VariantId, number> {
  // 既定は均等配分
  const defaults: Record<VariantId, number> = {
    existing: 0,
    designA: 1,
    designB: 1,
    designC: 1,
    designD: 0,
    designE: 1,
    designF: 1,
  };
  const w = { ...defaults, ...(input ?? {}) } as Record<VariantId, number>;
  const sum = VARIANT_IDS.reduce((acc, id) => acc + (w[id] ?? 0), 0);
  if (!sum) return defaults;
  const norm: Record<VariantId, number> = { ...w };
  VARIANT_IDS.forEach((id) => (norm[id] = (w[id] ?? 0) / sum));
  return norm;
}

/* ===========================
 * メインクラス
 * =========================== */

export class fvExperimentABTester {
  private storageKey: string;
  private experimentId: string;
  private weights: Record<VariantId, number>;
  private variants: Record<VariantId, VariantConfig>;

  constructor(opts?: Options) {
    this.storageKey = opts?.storageKey ?? "exp_lp_v2";
    this.experimentId = opts?.experimentId ?? "lp_v2";
    this.weights = normalizeWeights(opts?.weights);

    // デフォルトの7バリアント定義（必要に応じて文言/背景を差し替えてください）
    const defaults: VariantConfig[] = [
      // existing：従来FV
      {
        id: "existing",
        bg: { image: undefined },
        headline: [
          [
            {
              text: "あなたの不動産、\n今いくら？\nAIが価格を自動査定！",
              sx: baseSpan(32),
            },
          ],
        ],
        bullets: [
          [{ text: "最短60秒！", sx: baseSpan(20) }],
          [{ text: "東京限定＆完全無料！", sx: baseSpan(20) }],
          [{ text: "居住用・投資用どちらも対応", sx: baseSpan(20) }],
        ],
        cta: { placement: "below-bullets", align: "center", classSuffix: "fv" },
        containerSx: { height: "520px" },
      },

      // designA：現行
      {
        id: "designA",
        bg: {
          image: undefined,
          sx: {
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "primary.dark",
              opacity: 0.86,
              zIndex: 1,
            },
          },
        },
        headline: [
          [{ imageSrc: featureImage, alt: "上場AI企業HEROZ社と共同開発", sx: { width: "320px" } }],
          [{ text: "最先端AI査定を", sx: gradientSpan(32) }],
          [{ text: "Propallyで体験", sx: gradientSpan(32) }],
        ],
        bullets: [
          [{ text: "最短60秒！", sx: baseSpan(20) }],
          [{ text: "東京限定＆完全無料！", sx: baseSpan(20) }],
          [{ text: "居住用・投資用どちらも対応", sx: baseSpan(20) }],
        ],
        cta: { placement: "below-bullets", align: "center", classSuffix: "fv" },
        containerSx: { height: "560px" },
      },

      // designB：背景画像を明るくする
      {
        id: "designB",
        bg: {
          image: pattern_B,
        },
        headline: [
          [{ imageSrc: featureImage, alt: "上場AI企業HEROZ社と共同開発", sx: { width: "320px" } }],
          [{ text: "最先端AI査定を", sx: gradientSpan(32) }],
          [{ text: "Propallyで体験", sx: gradientSpan(32) }],
        ],
        bullets: [
          [{ text: "最短60秒！", sx: baseSpan(20) }],
          [{ text: "東京限定＆完全無料！", sx: baseSpan(20) }],
          [{ text: "居住用・投資用どちらも対応", sx: baseSpan(20) }],
        ],
        cta: { placement: "below-bullets", align: "center", classSuffix: "fv" },
        containerSx: { height: "560px" },
      },

      // designC：画像変更 青
      {
        id: "designC",
        bg: {
          image: pattern_C,
        },
        headline: [
          [{ imageSrc: featureImage, alt: "上場AI企業HEROZ社と共同開発", sx: { width: "320px" } }],
          [{ text: "最先端AI査定を", sx: baseSpan(32) }],
          [{ text: "Propallyで体験", sx: baseSpan(32) }],
        ],
        bullets: [
          [{ text: "最短60秒！", sx: baseSpan(20) }],
          [{ text: "東京限定＆完全無料！", sx: baseSpan(20) }],
          [{ text: "居住用・投資用どちらも対応", sx: baseSpan(20) }],
        ],
        cta: { placement: "below-bullets", align: "center", classSuffix: "fv" },
        containerSx: { height: "560px" },
      },

      // designD：画像変更 橙
      {
        id: "designD",
        bg: {
          image: pattern_D,
        },
        headline: [
          [{ imageSrc: featureImage, alt: "上場AI企業HEROZ社と共同開発", sx: { width: "320px" } }],
          [{ text: "最先端AI査定を", sx: baseSpan(32) }],
          [{ text: "Propallyで体験", sx: baseSpan(32) }],
        ],
        bullets: [
          [{ text: "最短60秒！", sx: baseSpan(20) }],
          [{ text: "東京限定＆完全無料！", sx: baseSpan(20) }],
          [{ text: "居住用・投資用どちらも対応", sx: baseSpan(20) }],
        ],
        cta: { placement: "below-bullets", align: "center", classSuffix: "fv" },
        containerSx: { height: "560px" },
      },

      // designC：背景白単色、CTA 中央、超シンプル
      {
        id: "designE",
        bg: {
          sx: {
            background: "#ffffff",
          },
        },
        headline: [
          [{ text: "あなたの不動産", sx: baseSpanBlack(30) }],
          [{ text: "今売るといくら？", sx: baseSpanBlack(30) }],
        ],
        bullets: [
          [{ text: "上場AI企業HEROZ社と共同開発", sx: baseSpanBlack(20) }],
          [{ text: "メディア掲載実績あり", sx: baseSpanBlack(20) }],
        ],
        cta: { placement: "above-bullets", align: "center", classSuffix: "fv" },
        containerSx: { height: "400px" },
        bulletsSx: { color: "#37474F"  }
      },

      // designF：背景=生活者写真、CTA 下・右寄せ
      {
        id: "designF",
        bg: { image: pattern_F },
        headline: [
          [{ text: "たった60秒で", sx: gradientSpan(34) }],
          [{ text: "あなたの物件の価値が", sx: gradientSpan(28) }],
          [{ text: "わかる", sx: gradientSpan(28) }],
        ],
        bullets: [],
        cta: { placement: "below-bullets", align: "left", classSuffix: "fv" },
        containerSx: { height: "450px"},
        ctaGap: 12
      },
    ];

    const arr = opts?.variants ?? defaults;
    this.variants = arr.reduce((acc, v) => ({ ...acc, [v.id]: v }), {} as Record<VariantId, VariantConfig>);
  }

  /** バリアント決定（?exp=... を優先。localStorage に保存） */
  getVariant(): VariantId {
    if (typeof window === "undefined") {
      // SSR時は重み最大のもの
      const sorted = [...VARIANT_IDS].sort((a, b) => this.weights[b] - this.weights[a]);
      return sorted[0];
    }
    const params = new URLSearchParams(window.location.search);
    const ov = params.get("exp");
    if (ov && (VARIANT_IDS as readonly string[]).includes(ov as VariantId)) {
      safeLocalStorageSet(this.storageKey, ov);
      return ov as VariantId;
    }
    const saved = safeLocalStorageGet(this.storageKey);
    if (saved && (VARIANT_IDS as readonly string[]).includes(saved as VariantId)) {
      return saved as VariantId;
    }
    const v = this.weightedPick();
    safeLocalStorageSet(this.storageKey, v);
    return v;
  }

  /** 計測（表示時） */
  trackView(variant: VariantId) {
    if (typeof window === "undefined") return;
    try {
      // @ts-expect-error gtag はグローバルに存在する場合だけ
      window.gtag?.("event", "exp_view", { experiment_id: this.experimentId, variant });
    } catch {
      // 特に何もしない
    }
    try {
      // @ts-expect-error clarity はグローバルに存在する場合だけ
      window.clarity?.("set", "exp_experiment_id", this.experimentId);
      // @ts-expect-error clarity はグローバルに存在する場合だけ
      window.clarity?.("set", "exp_variant", variant);
    } catch {
      // 特に何もしない
    }
  }

  /** VariantConfig を取得 */
  getConfig(v: VariantId): VariantConfig {
    return this.variants[v];
  }

  /** 互換API：見出し */
  getHeadline(v: VariantId): RichBlock {
    return this.getConfig(v).headline;
  }

  /** 互換API：bullets */
  getBullets(v: VariantId): RichLine[] {
    return this.getConfig(v).bullets;
  }

  /** 見出しリッチブロックを描画（行ごとに Typography） */
  static renderRichBlock(block: RichBlock, common?: React.ComponentProps<typeof Typography>) {
    const mergedCommon = {
      fontWeight: 700,
      color: "#FFF",
      lineHeight: "normal",
      // 改行（\n）を確実に効かせる
      sx: { whiteSpace: "pre-line", ...(common?.sx ?? {}) },
      ...common,
    } as React.ComponentProps<typeof Typography>;

    const isImageSpan = (s: RichSpan): s is RichImageSpan => "imageSrc" in s;

    return (
      <Stack spacing={0.5}>
        {block.map((line, idx) => (
          <Typography key={idx} {...mergedCommon}>
            {line.map((s, i) =>
              isImageSpan(s) ? (
                <Box
                  key={i}
                  component="img"
                  src={s.imageSrc}
                  alt={(s as RichImageSpan).alt ?? ""}
                  sx={(s as RichImageSpan).sx}
                />
              ) : (
                <Typography key={i} component="span" sx={(s as RichTextSpan).sx}>
                  {(s as RichTextSpan).text}
                </Typography>
              ),
            )}
          </Typography>
        ))}
      </Stack>
    );
  }

  /** 重みに基づき 1 つを抽選 */
  private weightedPick(): VariantId {
    const r = Math.random();
    let acc = 0;
    for (const id of VARIANT_IDS) {
      acc += this.weights[id];
      if (r < acc) return id;
    }
    // フォールバック
    return "designB";
  }
}

/* ---------- スタイル・ヘルパー ---------- */

export function baseSpan(fontPx: number): SxProps<Theme> {
  return {
    color: "#FFF",
    fontFamily: '"Noto Sans JP", sans-serif',
    fontSize: `${fontPx}px`,
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  };
}

export function baseSpanBlack(fontPx: number): SxProps<Theme> {
  return {
    color: "#37474F",
    fontFamily: '"Noto Sans JP", sans-serif',
    fontSize: `${fontPx}px`,
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  };
}

export function gradientSpan(fontPx: number): SxProps<Theme> {
  return {
    fontFamily: '"Noto Sans JP", sans-serif',
    fontSize: `${fontPx}px`,
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
    background: "linear-gradient(321deg, #FF6232 31.17%, #FF9347 79.27%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };
}
